import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../../../Components/Header/Header";
import MetaData from "../../../../../Components/MetaData";
import { Box, Button, IconButton, TextField } from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";
import MuiDataTable from "../../../../../Components/MUIDataTable/MUIDataTable";
import Modal from "../../../../../Components/SiteModal/SiteModal";
import { Col, Form, Row } from "react-bootstrap";
import { errorNotify, successNotify } from "../../../../../Utils/toast";
import { useDispatch, useSelector } from "react-redux";
import { FaEdit, FaEye } from "react-icons/fa";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  clearErrors,
  createTaxConfig,
  getTaxConfigs,
  updateTaxConfig,
} from "../../../../../Redux/Actions/Taxes";
import { useStyle } from "../../Attendance/attendance-styles";

const TaxConfigActionsCell = ({ rowId, rowIsActive, onView, onEdit }) => {
  return (
    <div className="leaves_management_btn_container">
      <button className="edit_btn" onClick={() => onView(rowId)} type="button">
        <FaEye />
      </button>
      {rowIsActive ? (
        <button className="edit_btn" onClick={() => onEdit(rowId)} type="button">
          <FaEdit />
        </button>
      ) : null}
    </div>
  );
};

const createSlabRow = () => ({
  id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
  min: "",
  max: "",
  fixedAmount: "",
  ratePercent: "",
  autoMin: false,
});

const preventInvalidNumberKeys = (e) => {
  // Prevent scientific notation and +/- in numeric inputs
  if (["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(e.key)) {
    e.preventDefault();
  }
};

const preventWheelNumberChange = (e) => {
  // Prevent mouse wheel from incrementing/decrementing focused number inputs
  e.target.blur();
};

const toNumOrNull = (v) =>
  v === "" || v === null || v === undefined ? null : Number(v);

const toNonNegNumOrZero = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  return Number(v);
};

const toNonNegNumOrNull = (v) => {
  const n = toNumOrNull(v);
  if (n === null) return null;
  return n;
};

const getPrevSlabMax = (slabs, idx) => {
  if (idx <= 0) return null;
  const prevMax = toNonNegNumOrNull(slabs[idx - 1]?.max);
  return prevMax === null ? null : Number(prevMax);
};

const getMaxMinConstraint = (minValue) => {
  const minNum = Number(minValue);
  return Number.isFinite(minNum) && minNum >= 0 ? minNum + 1 : 0;
};

const normalizeSlabsForValidation = (slabs) =>
  (slabs || [])
    .map((s, idx) => ({
      __idx: idx,
      min: Number(s.min),
      max: toNumOrNull(s.max),
      fixedAmount: toNonNegNumOrZero(s.fixedAmount),
      ratePercent: Number(s.ratePercent),
    }))
    .sort((a, b) => a.min - b.min);

const validateSlabRowBasics = (s, isLast) => {
  if (!Number.isFinite(s.min) || s.min < 0) return `Invalid min amount in slab ${s.__idx + 1}.`;
  if (s.max !== null && (!Number.isFinite(s.max) || s.max <= s.min))
    return `Invalid max amount in slab ${s.__idx + 1}.`;
  if (!Number.isFinite(s.fixedAmount) || s.fixedAmount < 0)
    return `Invalid fixed amount in slab ${s.__idx + 1}.`;
  if (!Number.isFinite(s.ratePercent) || s.ratePercent < 0 || s.ratePercent > 100)
    return `Invalid tax % in slab ${s.__idx + 1}.`;
  if (s.max === null && !isLast) return "Only the last slab can have an empty maximum.";
  return null;
};

const validateSlabRowAgainstPrev = (s, prev) => {
  if (!prev) return null;
  if (prev.max === null) return "No slabs can appear after an open-ended slab.";
  if (s.min <= prev.max) return "Slabs must not overlap and must be in increasing order.";
  return null;
};

const validateNormalizedSlabs = (sorted) => {
  if (!Array.isArray(sorted) || sorted.length === 0) {
    return "Please add at least one tax slab.";
  }

  for (let i = 0; i < sorted.length; i++) {
    const s = sorted[i];
    const isLast = i === sorted.length - 1;
    const basicsError = validateSlabRowBasics(s, isLast);
    if (basicsError) return basicsError;

    const prevError = validateSlabRowAgainstPrev(s, sorted[i - 1]);
    if (prevError) return prevError;
  }

  return null;
};

const TaxManagement = () => {
  const dispatch = useDispatch();
  const classes = useStyle();
  const { taxConfigs, error } = useSelector((state) => state.taxConfigs);
  const {
    loading: creating,
    success,
    message,
    error: createError,
  } = useSelector((state) => state.createTaxConfig);
  const {
    loading: updating,
    success: updateSuccess,
    message: updateMessage,
    error: updateError,
  } = useSelector((state) => state.updateTaxConfig);

  // Table state (MuiDataTable expects these)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");

  const [show, setShow] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [showView, setShowView] = useState(false);
  const [viewConfig, setViewConfig] = useState(null);
  const [effectiveFromDate, setEffectiveFromDate] = useState(new Date());
  const [slabs, setSlabs] = useState([createSlabRow()]);
  const [fieldErrors, setFieldErrors] = useState({});

  const setSlabFieldError = (slabId, field, message) => {
    setFieldErrors((prev) => ({
      ...prev,
      [slabId]: {
        ...(prev[slabId] || undefined),
        [field]: message,
      },
    }));
  };

  const clearSlabFieldError = (slabId, field) => {
    setFieldErrors((prev) => {
      if (!prev[slabId]?.[field]) return prev;
      return {
        ...prev,
        [slabId]: {
          ...(prev[slabId] || undefined),
          [field]: null,
        },
      };
    });
  };

  const validateMinField = ({ slabId, min, prevMax }) => {
    if (!Number.isFinite(min) || min < 0) {
      setSlabFieldError(slabId, "min", "Min must be a non-negative number.");
      return;
    }
    if (prevMax !== null && Number.isFinite(prevMax) && min <= prevMax) {
      setSlabFieldError(slabId, "min", "Min must be greater than previous slab max.");
      return;
    }
    clearSlabFieldError(slabId, "min");
  };

  const validateMaxField = ({ slabId, min, max, isLast }) => {
    if (max === null) {
      if (isLast) {
        clearSlabFieldError(slabId, "max");
      } else {
        setSlabFieldError(slabId, "max", "Only the last slab can have an empty max.");
      }
      return;
    }
    if (!Number.isFinite(max) || max < 0) {
      setSlabFieldError(slabId, "max", "Max must be a non-negative number.");
      return;
    }
    if (!Number.isFinite(min) || min < 0) {
      setSlabFieldError(slabId, "max", "Set a valid min first.");
      return;
    }
    if (max <= min) {
      setSlabFieldError(slabId, "max", "Max must be greater than min.");
      return;
    }
    clearSlabFieldError(slabId, "max");
  };

  const validateNextMinAgainstMax = ({ nextSlab, max }) => {
    if (!nextSlab) return;
    const nextMin = Number(nextSlab.min);
    if (max !== null && Number.isFinite(max) && Number.isFinite(nextMin) && nextMin <= max) {
      setSlabFieldError(nextSlab.id, "min", "Min must be greater than previous slab max.");
    } else {
      clearSlabFieldError(nextSlab.id, "min");
    }
  };

  const validateFixedAmountField = ({ slabId, fixedAmount }) => {
    if (!Number.isFinite(fixedAmount) || fixedAmount < 0) {
      setSlabFieldError(slabId, "fixedAmount", "Fixed amount must be 0 or more.");
    } else {
      clearSlabFieldError(slabId, "fixedAmount");
    }
  };

  const validateRatePercentField = ({ slabId, ratePercent }) => {
    if (!Number.isFinite(ratePercent) || ratePercent < 0 || ratePercent > 100) {
      setSlabFieldError(slabId, "ratePercent", "Percentage must be between 0 and 100.");
    } else {
      clearSlabFieldError(slabId, "ratePercent");
    }
  };

  const validateSlabField = (slabsArr, idx, field) => {
    const slab = slabsArr[idx];
    if (!slab) return;

    const slabId = slab.id;
    const next = idx < slabsArr.length - 1 ? slabsArr[idx + 1] : null;
    const isLast = idx === slabsArr.length - 1;

    const min = Number(slab.min);
    const max = slab.max === "" ? null : Number(slab.max);
    const fixedAmount = slab.fixedAmount === "" ? 0 : Number(slab.fixedAmount);
    const ratePercent = Number(slab.ratePercent);
    const prevMax = getPrevSlabMax(slabsArr, idx);

    switch (field) {
      case "min":
        validateMinField({ slabId, min, prevMax });
        // If max exists, keep max/min relationship valid too
        if (max !== null && Number.isFinite(max) && Number.isFinite(min) && max <= min) {
          setSlabFieldError(slabId, "max", "Max must be greater than min.");
        }
        break;
      case "max":
        validateMaxField({ slabId, min, max, isLast });
        validateNextMinAgainstMax({ nextSlab: next, max });
        break;
      case "fixedAmount":
        validateFixedAmountField({ slabId, fixedAmount });
        break;
      case "ratePercent":
        validateRatePercentField({ slabId, ratePercent });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    dispatch(getTaxConfigs());
  }, []);

  const openCreateModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setFieldErrors({});
    setEffectiveFromDate(new Date());
    setSlabs([createSlabRow()]);
    setShow(true);
  };

  const openViewModal = (id) => {
    const cfg = (taxConfigs || []).find((t) => t?._id === id);
    setViewConfig(cfg || null);
    setShowView(true);
  };

  const openEditModal = (id) => {
    const cfg = (taxConfigs || []).find((t) => t?._id === id);
    if (!cfg) return;
    if (!cfg.isActive) {
      errorNotify("Only the active tax configuration can be edited.");
      return;
    }

    setIsEditing(true);
    setEditingId(cfg._id);
    setFieldErrors({});
    setEffectiveFromDate(new Date(cfg.effectiveFromYear, 0, 1));
    setSlabs(
      (cfg.slabs || []).map((s) => ({
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        min: s.min ?? "",
        max: s.max ?? "",
        fixedAmount: s.fixed ?? 0,
        ratePercent: Number.isFinite(Number(s.rate)) ? Number(s.rate) * 100 : "",
        autoMin: false,
      })),
    );
    setShow(true);
  };

  useEffect(() => {
    if (error) {
      errorNotify(error);
      dispatch(clearErrors());
    }
    if (createError) {
      errorNotify(createError);
      dispatch(clearErrors());
    }
    if (success) {
      successNotify(message || "Tax configuration created.");
      dispatch({ type: "CREATE_TAX_CONFIG_RESET" });
      setShow(false);
      setIsEditing(false);
      setEditingId(null);
      dispatch(getTaxConfigs());
    }
  }, [error, createError, success, message]);

  useEffect(() => {
    if (updateError) {
      errorNotify(updateError);
      dispatch(clearErrors());
    }
    if (updateSuccess) {
      successNotify(updateMessage || "Tax configuration updated.");
      dispatch({ type: "UPDATE_TAX_CONFIG_RESET" });
      setShow(false);
      setIsEditing(false);
      setEditingId(null);
      dispatch(getTaxConfigs());
    }
  }, [updateError, updateSuccess, updateMessage]);

  const handleAddSlabRow = () => {
    setSlabs((prev) => {
      if (prev.length === 0) return [createSlabRow()];

      const last = prev.at(-1);
      const lastMaxRaw = last?.max;
      const lastMax =
        lastMaxRaw === "" || lastMaxRaw === null || lastMaxRaw === undefined
          ? null
          : Number(lastMaxRaw);

      if (lastMax === null) {
        errorNotify("You can't add a new slab after an open-ended (no max) slab.");
        return prev;
      }

      if (!Number.isFinite(lastMax) || lastMax < 0) {
        errorNotify("Please enter a valid max for the previous slab first.");
        return prev;
      }

      const next = createSlabRow();
      next.min = String(lastMax + 1);
      next.autoMin = true;

      // Best-effort auto-fill fixed amount for the next slab
      const lastMin = Number(last?.min);
      const lastRate = Number(last?.ratePercent) / 100;
      const lastFixed = Number(last?.fixedAmount ?? 0);
      if (
        Number.isFinite(lastMin) &&
        Number.isFinite(lastRate) &&
        lastRate >= 0 &&
        Number.isFinite(lastFixed) &&
        lastFixed >= 0 &&
        lastMax > lastMin
      ) {
        const nextFixed = lastFixed + (lastMax - lastMin) * lastRate;
        next.fixedAmount = String(Math.round(nextFixed));
      }

      return [...prev, next];
    });
  };

  const handleRemoveSlabRow = (idx) => {
    setSlabs((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSlabChange = (idx, field, value) => {
    setSlabs((prev) => {
      const next = prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s));

      // If user manually edits min, stop auto-linking it to previous max.
      if (field === "min" && next[idx]) {
        next[idx].autoMin = false;
      }

      // If a slab's max changes, and the next slab has autoMin, keep it synced to max + 1.
      if (field === "max" && next[idx] && next[idx + 1]?.autoMin) {
        const maxNum = Number(value);
        if (Number.isFinite(maxNum)) {
          next[idx + 1].min = String(maxNum + 1);
        }
      }

      return next;
    });

    // Optimistically clear the error for the field while typing.
    const slabId = slabs[idx]?.id;
    if (slabId) {
      clearSlabFieldError(slabId, field);
    }
  };

  const validateSlabs = () => {
    return validateNormalizedSlabs(normalizeSlabsForValidation(slabs));
  };

  const validateBeforeSubmit = () => {
    if (!(effectiveFromDate instanceof Date) || Number.isNaN(effectiveFromDate.getTime())) {
      errorNotify("Please select a valid year.");
      return false;
    }
    const yearNum = effectiveFromDate.getFullYear();
    if (!Number.isFinite(yearNum) || yearNum < 1900) {
      errorNotify("Please select a valid year.");
      return false;
    }
    const slabError = validateSlabs();
    if (slabError) {
      errorNotify(slabError);
      return false;
    }
    return true;
  };

  const handleCreateTaxConfig = async () => {
    if (!validateBeforeSubmit()) return;

    const payload = {
      effectiveFromYear: effectiveFromDate.getFullYear(),
      slabs: slabs.map((s) => ({
        min: Number(s.min),
        max: s.max === "" ? null : Number(s.max),
        fixedAmount: s.fixedAmount === "" ? 0 : Number(s.fixedAmount),
        ratePercent: Number(s.ratePercent),
      })),
    };

    if (isEditing && editingId) {
      dispatch(updateTaxConfig(editingId, { slabs: payload.slabs }));
    } else {
      dispatch(createTaxConfig(payload));
    }
  };

  const taxRows = useMemo(() => {
    return (taxConfigs || []).map((t) => ({
      _id: t._id,
      isActive: Boolean(t.isActive),
      effectiveFromYear: t.effectiveFromYear,
      effectiveToYear: t.effectiveToYear ?? "-",
      status: t.isActive ? "Active" : "Inactive",
      slabsCount: Array.isArray(t.slabs) ? t.slabs.length : 0,
      createdAt: t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "-",
    }));
  }, [taxConfigs]);

  const taxColumns = [
    {
      name: "_id",
      options: { display: false, print: false, download: false },
    },
    {
      name: "isActive",
      options: { display: false, print: false, download: false },
    },
    { name: "effectiveFromYear", label: "From (Year)" },
    { name: "effectiveToYear", label: "To (Year)" },
    { name: "status", label: "Status" },
    { name: "slabsCount", label: "Slabs" },
    { name: "createdAt", label: "Created" },
    {
      name: "action",
      label: "Action",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowId = tableMeta?.rowData?.[0];
          const rowIsActive = Boolean(tableMeta?.rowData?.[1]);
          return (
            <TaxConfigActionsCell
              rowId={rowId}
              rowIsActive={rowIsActive}
              onView={openViewModal}
              onEdit={openEditModal}
            />
          );
        },
      },
    },
  ];

  const createModal = (
    <Modal
      title={isEditing ? "Edit active tax configuration" : "Set tax for new period"}
      size={"lg"}
      show={show}
      onCloseModal={() => setShow(false)}
    >
      <div style={{ padding: "0 24px" }}>
        <Row className="gy-4">
          <Col md={4}>
            <Form.Label style={{ marginBottom: 8, fontWeight: 600 }}>
              Effective from (Year)
            </Form.Label>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                views={["year"]}
                openTo="year"
                value={effectiveFromDate}
                onChange={(next) => setEffectiveFromDate(next || new Date(""))}
                disabled={isEditing}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </LocalizationProvider>
            {isEditing ? (
              <div style={{ marginTop: 6, fontSize: 12, color: "rgba(0,0,0,0.7)" }}>
                Effective year can’t be changed in edit mode.
              </div>
            ) : null}
          </Col>

          <Col md={12}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingTop: 8,
              }}
            >
              <h6 style={{ margin: 0, fontWeight: 700 }}>Salary slabs (annual)</h6>
              <Button variant="contained" onClick={handleAddSlabRow}>
                <Add /> Add slab
              </Button>
            </div>
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(0,0,0,0.02)",
                color: "rgba(0,0,0,0.75)",
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              <div style={{ fontWeight: 700, color: "#253766" }}>
                How tax is calculated in a slab
              </div>
              <div style={{ marginTop: 4 }}>
                Tax = <b>Fixed amount</b> + (<b>Annual salary</b> − <b>Slab Min</b>) ×{" "}
                <b>Percentage</b>
              </div>
              <div style={{ marginTop: 4 }}>
                The salary must fall within <b>Slab Min</b> and <b>Slab Max</b> (Max can be
                empty only for the last slab).
              </div>
              <div style={{ marginTop: 4 }}>
                <b>Fixed amount</b> is optional (leave empty to treat it as 0).
              </div>
            </div>
          </Col>

          <Col md={12}>
            {slabs.map((s, idx) => {
              const isLast = idx === slabs.length - 1;
              const prevMax = getPrevSlabMax(slabs, idx);
              const maxMinConstraint = getMaxMinConstraint(s.min);
              return (
                <Row
                  key={s.id}
                  className="gy-2 align-items-start"
                  style={{
                    padding: "12px 12px",
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 10,
                    marginBottom: 10,
                    background: "rgba(0,0,0,0.02)",
                  }}
                >
                  <Col md={3}>
                    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                      <Form.Label
                        style={{
                          marginBottom: 6,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Slab Min
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={s.min}
                        onChange={(e) => handleSlabChange(idx, "min", e.target.value)}
                        onBlur={() => validateSlabField(slabs, idx, "min")}
                        onKeyDown={preventInvalidNumberKeys}
                        onWheel={preventWheelNumberChange}
                        min={
                          idx > 0 && Number.isFinite(prevMax) ? Math.max(0, prevMax + 1) : 0
                        }
                        placeholder="e.g. 600001"
                        isInvalid={Boolean(fieldErrors?.[s.id]?.min)}
                      />
                      <small
                        style={{
                          display: "block",
                          marginTop: 6,
                          color: "rgba(0,0,0,0.6)",
                          minHeight: 18,
                        }}
                      >
                        Lower bound (exclusive).
                      </small>
                      {fieldErrors?.[s.id]?.min ? (
                        <div style={{ color: "#c62828", fontSize: 12, marginTop: 4 }}>
                          {fieldErrors[s.id].min}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col md={3}>
                    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                      <Form.Label
                        style={{
                          marginBottom: 6,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Slab Max
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={s.max}
                        onChange={(e) => handleSlabChange(idx, "max", e.target.value)}
                        onBlur={() => validateSlabField(slabs, idx, "max")}
                        onKeyDown={preventInvalidNumberKeys}
                        onWheel={preventWheelNumberChange}
                        min={maxMinConstraint}
                        placeholder={
                          isLast ? "Optional" : ""
                        }
                        isInvalid={Boolean(fieldErrors?.[s.id]?.max)}
                      />
                      <small
                        style={{
                          display: "block",
                          marginTop: 6,
                          color: "rgba(0,0,0,0.6)",
                          minHeight: 18,
                        }}
                      >
                        Upper bound (inclusive).
                      </small>
                      {fieldErrors?.[s.id]?.max ? (
                        <div style={{ color: "#c62828", fontSize: 12, marginTop: 4 }}>
                          {fieldErrors[s.id].max}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col md={3}>
                    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                      <Form.Label
                        style={{
                          marginBottom: 6,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Fixed Amount
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={s.fixedAmount}
                        onChange={(e) =>
                          handleSlabChange(idx, "fixedAmount", e.target.value)
                        }
                        onBlur={() => validateSlabField(slabs, idx, "fixedAmount")}
                        onKeyDown={preventInvalidNumberKeys}
                        onWheel={preventWheelNumberChange}
                        min={0}
                        placeholder="Optional — e.g. 6000"
                        isInvalid={Boolean(fieldErrors?.[s.id]?.fixedAmount)}
                      />
                      <small
                        style={{
                          display: "block",
                          marginTop: 6,
                          color: "rgba(0,0,0,0.6)",
                          minHeight: 18,
                        }}
                      >
                        Base tax for this slab (empty = 0).
                      </small>
                      {fieldErrors?.[s.id]?.fixedAmount ? (
                        <div style={{ color: "#c62828", fontSize: 12, marginTop: 4 }}>
                          {fieldErrors[s.id].fixedAmount}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col md={2}>
                    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                      <Form.Label
                        style={{
                          marginBottom: 6,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Percentage (%)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={s.ratePercent}
                        onChange={(e) =>
                          handleSlabChange(idx, "ratePercent", e.target.value)
                        }
                        onBlur={() => validateSlabField(slabs, idx, "ratePercent")}
                        onKeyDown={preventInvalidNumberKeys}
                        onWheel={preventWheelNumberChange}
                        min={0}
                        max={100}
                        step={0.01}
                        placeholder="e.g. 11"
                        isInvalid={Boolean(fieldErrors?.[s.id]?.ratePercent)}
                      />
                      <small
                        style={{
                          display: "block",
                          marginTop: 6,
                          color: "rgba(0,0,0,0.6)",
                          minHeight: 18,
                        }}
                      >
                        Applied on (salary − Min).
                      </small>
                      {fieldErrors?.[s.id]?.ratePercent ? (
                        <div style={{ color: "#c62828", fontSize: 12, marginTop: 4 }}>
                          {fieldErrors[s.id].ratePercent}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col md={1} style={{ display: "flex", justifyContent: "end" }}>
                    <IconButton
                      onClick={() => handleRemoveSlabRow(idx)}
                      disabled={slabs.length === 1}
                      aria-label="remove slab"
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Col>
                </Row>
              );
            })}
          </Col>

          <Col md={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "10px",
              }}
            >
              <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  type="button"
                  className={classes.cancelBtn}
                  onClick={() => setShow(false)}
                  disabled={creating || updating}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="button"
                  disabled={creating || updating}
                  className={classes.createLeaveBtn}
                  onClick={handleCreateTaxConfig}
                >
                  {creating || updating ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
          </Col>
        </Row>
      </div>
    </Modal>
  );

  const viewModal = (
    <Modal
      title="Tax configuration"
      size={"lg"}
      show={showView}
      onCloseModal={() => setShowView(false)}
    >
      <div style={{ padding: "0 24px 16px" }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 700 }}>Effective period</div>
            <div style={{ marginTop: 4 }}>
              {viewConfig?.effectiveFromYear} — {viewConfig?.effectiveToYear ?? "Present"}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 700 }}>Status</div>
            <div style={{ marginTop: 4 }}>{viewConfig?.isActive ? "Active" : "Inactive"}</div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Salary slabs (annual)</div>
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Min</th>
                  <th>Max</th>
                  <th>Fixed</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {(viewConfig?.slabs || []).map((s, i) => (
                  <tr key={`${viewConfig?._id || "tax"}_${i}`}>
                    <td>{i + 1}</td>
                    <td>{s?.min ?? "-"}</td>
                    <td>{s?.max ?? "-"}</td>
                    <td>{s?.fixed ?? 0}</td>
                    <td>
                      {Number.isFinite(Number(s?.rate)) ? `${Number(s.rate) * 100}` : "-"}
                    </td>
                  </tr>
                ))}
                {(viewConfig?.slabs || []).length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", color: "rgba(0,0,0,0.6)" }}>
                      No slabs found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="modal_decision" style={{ marginTop: 14 }}>
          <button className="my-1 no_btn" onClick={() => setShowView(false)} type="button">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );

  return (
    <div className="page_responsive">
      <MetaData title={`Tax Management -- HRM`} />
      <Header title="TAX MANAGEMENT" />
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={(e) => {
              e.preventDefault();
              openCreateModal();
            }}
            style={{
              padding: "10px 20px",
              background: "#ee1c25",
              boxShadow: "0 15px 20px 0 rgb(13 110 253 / 30%)",
              border: "none",
              fontSize: "12px",
              fontWeight: 500,
              borderRadius: "10px",
              color: "white",
            }}
            type="button"
          >
            Set tax for new period
          </button>
        </div>

        <div style={{ marginTop: 12 }}>
          <MuiDataTable
            tableId={"tax_configs"}
            data={taxRows}
            columns={taxColumns}
            // title={loading ? "Loading..." : "Tax configurations"}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            count={taxRows.length}
            text={searchText}
            setText={setSearchText}
          />
        </div>
      </div>

      {show && createModal}
      {showView && viewModal}
    </div>
  );
};

export default TaxManagement;

